import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Dialog, DialogContent } from '@mui/material';
import { VideoCall as VideoCallIcon, CallEnd, Mic, MicOff, Videocam, VideocamOff } from '@mui/icons-material';
import { ChimeSDKMeetingsClient, CreateMeetingCommand, CreateAttendeeCommand, DeleteMeetingCommand } from "@aws-sdk/client-chime-sdk-meetings";
import { TranscribeStreamingClient, StartMedicalStreamTranscriptionCommand } from "@aws-sdk/client-transcribe-streaming";
import { GetAWSCredentials } from '../../../../api';

export default function VideoCall({ socket, conversationId, conversation, user, pendingAnsweredCall, onAnsweredCallHandled, onTranscriptReady }) {
  const [isInCall, setIsInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [dragPosition, setDragPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0 });
  
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const meetingRef = useRef(null);
  const audioVideoRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const observerRef = useRef(null);
  const cleanupRef = useRef(false);
  const transcribeClientRef = useRef(null);

  const initializeAWS = async () => {
    const credentials = await GetAWSCredentials();
    
    // Initialize Transcribe client
    transcribeClientRef.current = new TranscribeStreamingClient({
      region: credentials.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
      }
    });
    
    return new ChimeSDKMeetingsClient({
      region: credentials.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey
      }
    });
  };

  const cleanup = useCallback(async () => {
    if (cleanupRef.current) return;
    cleanupRef.current = true;

    try {
      // Stop media tracks first
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
        mediaStreamRef.current = null;
      }

      // Stop Chime SDK
      if (audioVideoRef.current?.audioVideo) {
        if (observerRef.current) {
          audioVideoRef.current.audioVideo.removeObserver(observerRef.current);
        }
        try {
          audioVideoRef.current.audioVideo.stopVideoInput();
          audioVideoRef.current.audioVideo.stopAudioInput();
          audioVideoRef.current.audioVideo.stop();
        } catch (e) {}
      }

      // Delete meeting
      if (meetingRef.current?.client && meetingRef.current?.meeting) {
        try {
          await meetingRef.current.client.send(new DeleteMeetingCommand({
            MeetingId: meetingRef.current.meeting.MeetingId
          }));
        } catch (e) {}
      }

      // Send transcript
      if (transcript.length > 0) {
        const transcriptText = transcript.map(t => `[${new Date(t.timestamp).toLocaleTimeString()}] ${t.speaker}: ${t.text}`).join('\n');
        onTranscriptReady?.(transcriptText);
      }

      socket.emit('video_call_ended');
    } catch (error) {
      console.error('Cleanup error:', error);
    } finally {
      // Reset all state
      setIsInCall(false);
      setTranscript([]);
      setIsMuted(false);
      setIsVideoOff(false);
      audioVideoRef.current = null;
      observerRef.current = null;
      meetingRef.current = null;
      transcribeClientRef.current = null;
      // Don't reset cleanupRef here - let startCall reset it
    }
  }, [socket, transcript, onTranscriptReady]);

  const setupVideoSession = useCallback(async () => {
    if (!meetingRef.current?.meeting || !meetingRef.current?.attendee) {
      throw new Error('Meeting data missing');
    }

    const ChimeSDK = await import('amazon-chime-sdk-js');
    const { DefaultMeetingSession, ConsoleLogger, LogLevel, MeetingSessionConfiguration, DefaultDeviceController } = ChimeSDK;
    
    const logger = new ConsoleLogger('VideoCall', LogLevel.WARN);
    const deviceController = new DefaultDeviceController(logger);
    const configuration = new MeetingSessionConfiguration(meetingRef.current.meeting, meetingRef.current.attendee);
    const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
    
    audioVideoRef.current = meetingSession;

    // Check if cleanup happened during session creation
    if (cleanupRef.current || !audioVideoRef.current?.audioVideo) {
      throw new Error('Call was ended during setup');
    }

    // Get devices with null checks
    let videoDevices = [];
    let audioDevices = [];
    
    try {
      if (audioVideoRef.current?.audioVideo) {
        videoDevices = await audioVideoRef.current.audioVideo.listVideoInputDevices();
        audioDevices = await audioVideoRef.current.audioVideo.listAudioInputDevices();
      }
    } catch (e) {
      if (cleanupRef.current) {
        throw new Error('Call was ended during setup');
      }
      throw e;
    }
    
    // Check again before device setup
    if (cleanupRef.current || !audioVideoRef.current?.audioVideo) {
      throw new Error('Call was ended during setup');
    }
    
    if (videoDevices.length > 0 && audioVideoRef.current?.audioVideo) {
      await audioVideoRef.current.audioVideo.startVideoInput(videoDevices[0].deviceId);
    }
    if (audioDevices.length > 0 && audioVideoRef.current?.audioVideo) {
      await audioVideoRef.current.audioVideo.startAudioInput(audioDevices[0].deviceId);
    }
    
    // Start audio output
    if (audioVideoRef.current?.audioVideo) {
      await audioVideoRef.current.audioVideo.bindAudioElement(new Audio());
    }

    // Check again before setting up observer
    if (cleanupRef.current || !audioVideoRef.current?.audioVideo) {
      throw new Error('Call was ended during setup');
    }

    // Setup observer
    observerRef.current = {
      videoTileDidUpdate: (tileState) => {
        if (!audioVideoRef.current?.audioVideo) return;
        
        if (tileState.localTile && localVideoRef.current) {
          audioVideoRef.current.audioVideo.bindVideoElement(tileState.tileId, localVideoRef.current);
        } else if (!tileState.localTile && tileState.boundAttendeeId && remoteVideoRef.current) {
          audioVideoRef.current.audioVideo.bindVideoElement(tileState.tileId, remoteVideoRef.current);
        }
      },
      audioVideoDidStop: () => {
        if (!cleanupRef.current) {
          cleanup();
        }
      }
    };
    
    if (audioVideoRef.current?.audioVideo) {
      audioVideoRef.current.audioVideo.addObserver(observerRef.current);
      await audioVideoRef.current.audioVideo.start();
    }
    
    // Check before getting user media
    if (cleanupRef.current) {
      throw new Error('Call was ended during setup');
    }
    
    // Get user media
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    mediaStreamRef.current = stream;
    
    // Final check before starting video tile
    if (cleanupRef.current || !audioVideoRef.current?.audioVideo) {
      throw new Error('Call was ended during setup');
    }
    
    await audioVideoRef.current.audioVideo.startLocalVideoTile();
  }, [cleanup]);

  const startMedicalTranscription = useCallback(async () => {
    if (!transcribeClientRef.current || !mediaStreamRef.current) return;
    
    try {
      // Create audio context to process stream
      const audioContext = new AudioContext({ sampleRate: 16000 });
      const source = audioContext.createMediaStreamSource(mediaStreamRef.current);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      let streamController = null;
      
      // Convert audio to PCM format for AWS
      const audioStream = new ReadableStream({
        start(controller) {
          streamController = controller;
          processor.onaudioprocess = (e) => {
            if (streamController) {
              const inputData = e.inputBuffer.getChannelData(0);
              const pcmData = new Int16Array(inputData.length);
              
              for (let i = 0; i < inputData.length; i++) {
                pcmData[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
              }
              
              try {
                streamController.enqueue(new Uint8Array(pcmData.buffer));
              } catch (err) {
                // Stream closed, ignore
              }
            }
          };
        },
        cancel() {
          streamController = null;
          processor.disconnect();
          source.disconnect();
          audioContext.close();
        }
      });
      
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Start medical transcription
      const command = new StartMedicalStreamTranscriptionCommand({
        LanguageCode: 'en-US',
        MediaSampleRateHertz: 16000,
        MediaEncoding: 'pcm',
        Specialty: 'PRIMARYCARE',
        Type: 'CONVERSATION',
        ShowSpeakerLabel: true,
        MaxSpeakerLabels: Math.min(conversation?.participants?.length || 2, 10),
        AudioStream: (async function* () {
          const reader = audioStream.getReader();
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              yield { AudioEvent: { AudioChunk: value } };
            }
          } finally {
            reader.releaseLock();
          }
        })()
      });
      
      const response = await transcribeClientRef.current.send(command);
      
      // Process transcription results
      for await (const event of response.TranscriptResultStream) {
        if (event.TranscriptEvent?.Transcript?.Results) {
          const results = event.TranscriptEvent.Transcript.Results;
          
          for (const result of results) {
            if (result.IsPartial === false && result.Alternatives?.[0]?.Transcript) {
              const transcriptText = result.Alternatives[0].Transcript;
              const speakerLabel = result.Alternatives[0]?.Items?.[0]?.Speaker || 'Unknown';
              
              // Map speakers to proper names for group conversations
              let speakerName;
              if (conversation?.participants && user) {
                const currentUserName = user.name || 'You';
                const otherParticipants = conversation.participants.filter(p => p.id !== user.id);
                
                // Assign names based on speaker labels
                if (speakerLabel === 'spk_0') {
                  speakerName = currentUserName;
                } else {
                  // For other speakers, try to map to participant names
                  const speakerIndex = parseInt(speakerLabel.replace('spk_', '')) - 1;
                  if (speakerIndex >= 0 && speakerIndex < otherParticipants.length) {
                    speakerName = otherParticipants[speakerIndex]?.name || `Speaker ${speakerIndex + 1}`;
                  } else {
                    speakerName = `Speaker ${speakerLabel.replace('spk_', '')}`;
                  }
                }
              } else {
                // For global calls, use current user name for spk_0, generic names for others
                if (speakerLabel === 'spk_0') {
                  speakerName = user?.name || 'You';
                } else {
                  speakerName = `Speaker ${speakerLabel.replace('spk_', '')}`;
                }
              }
              
              setTranscript(prev => [...prev, {
                text: transcriptText,
                speaker: speakerName,
                timestamp: Date.now()
              }]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Medical transcription error:', error);
    }
  }, [user, conversation]);

  const startCall = async () => {
    // Reset cleanup flag for new call attempt
    cleanupRef.current = false;
    
    try {
      const meetingsClient = await initializeAWS();
      
      const meetingResponse = await meetingsClient.send(new CreateMeetingCommand({
        ClientRequestToken: `${Date.now()}`,
        ExternalMeetingId: `chat-${conversationId}-${Date.now()}`,
        MediaRegion: 'us-east-1'
      }));
      
      const attendeeResponse = await meetingsClient.send(new CreateAttendeeCommand({
        MeetingId: meetingResponse.Meeting.MeetingId,
        ExternalUserId: `${user.name}-${user.id}` // Include name in external user ID
      }));

      meetingRef.current = {
        meeting: meetingResponse.Meeting,
        attendee: attendeeResponse.Attendee,
        client: meetingsClient
      };

      socket.emit('video_call_ring', {
        to: conversation?.participants?.find(p => p.id !== user.id)?.id,
        meetingId: meetingResponse.Meeting.MeetingId,
        meetingData: meetingResponse.Meeting
      });

      await setupVideoSession();
      setIsInCall(true);
      
      // Start medical transcription
      setTimeout(() => {
        startMedicalTranscription();
      }, 2000); // Wait for audio to stabilize
    } catch (error) {
      console.error('Failed to start call:', error);
      
      // Don't show alert for declined calls - this is expected behavior
      if (error.message !== 'Call was ended during setup') {
        cleanup();
        alert('Failed to start video call. Please try again.');
      }
      // If call was declined, cleanup already happened, just return silently
    }
  };

  const answerCall = useCallback(async (callData) => {
    // Reset cleanup flag for new call attempt
    cleanupRef.current = false;
    
    try {
      const meetingsClient = await initializeAWS();
      
      const attendeeResponse = await meetingsClient.send(new CreateAttendeeCommand({
        MeetingId: callData.meetingData.MeetingId,
        ExternalUserId: `${user.name}-${user.id}` // Include name in external user ID
      }));

      meetingRef.current = {
        meeting: callData.meetingData,
        attendee: attendeeResponse.Attendee,
        client: meetingsClient
      };

      await setupVideoSession();
      setIsInCall(true);
      
      // Start medical transcription
      setTimeout(() => {
        startMedicalTranscription();
      }, 2000); // Wait for audio to stabilize
    } catch (error) {
      console.error('Failed to answer call:', error);
      cleanup();
      alert('Failed to answer video call. Please try again.');
    }
  }, [user.id, user.name, cleanup, setupVideoSession, startMedicalTranscription]);

  const endCall = () => {
    cleanup();
  };

  const toggleMute = () => {
    if (audioVideoRef.current) {
      if (isMuted) {
        audioVideoRef.current.audioVideo.realtimeUnmuteLocalAudio();
      } else {
        audioVideoRef.current.audioVideo.realtimeMuteLocalAudio();
      }
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (audioVideoRef.current) {
      if (isVideoOff) {
        audioVideoRef.current.audioVideo.startLocalVideoTile();
      } else {
        audioVideoRef.current.audioVideo.stopLocalVideoTile();
      }
      setIsVideoOff(!isVideoOff);
    }
  };

  // Handle pending answered call
  useEffect(() => {
    if (pendingAnsweredCall && !isInCall && 
        (pendingAnsweredCall.conversationId === conversationId || conversationId === null)) {
      answerCall(pendingAnsweredCall).finally(() => {
        onAnsweredCallHandled?.();
      });
    }
  }, [pendingAnsweredCall, conversationId, isInCall, onAnsweredCallHandled, answerCall]);

  // Handle socket events
  useEffect(() => {
    if (!socket) return;

    const handleCallEnded = () => cleanup();
    const handleCallDeclined = () => cleanup();

    socket.on('video_call_ended', handleCallEnded);
    socket.on('video_call_declined', handleCallDeclined);

    return () => {
      socket.off('video_call_ended', handleCallEnded);
      socket.off('video_call_declined', handleCallDeclined);
    };
  }, [socket, cleanup]);

  return (
    <>
      {!isInCall && conversation?.participants?.some(p => p.id !== user.id && p.isOnline) && (
        <IconButton onClick={startCall} size="small">
          <VideoCallIcon />
        </IconButton>
      )}

      <Dialog open={isInCall} fullScreen>
        <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
          <video
            ref={remoteVideoRef}
            autoPlay
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <video
            ref={localVideoRef}
            autoPlay
            muted
            onMouseDown={(e) => {
              setIsDragging(true);
              dragRef.current = { startX: e.clientX - dragPosition.x, startY: e.clientY - dragPosition.y };
            }}
            onMouseMove={(e) => {
              if (isDragging) {
                const newX = Math.max(0, Math.min(window.innerWidth - 200, e.clientX - dragRef.current.startX));
                const newY = Math.max(0, Math.min(window.innerHeight - 150, e.clientY - dragRef.current.startY));
                setDragPosition({ x: newX, y: newY });
              }
            }}
            onMouseUp={() => setIsDragging(false)}
            style={{
              position: 'absolute',
              top: dragPosition.y,
              right: 'auto',
              left: dragPosition.x,
              width: 200,
              height: 150,
              objectFit: 'cover',
              borderRadius: 8,
              zIndex: 10,
              border: '2px solid white',
              backgroundColor: 'black',
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
          />
          <Box sx={{
            position: 'absolute',
            bottom: { xs: 10, sm: 20 },
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            pb: { xs: 'env(safe-area-inset-bottom)', sm: 0 }
          }}>
            <IconButton 
              onClick={toggleMute} 
              sx={{ 
                bgcolor: isMuted ? 'error.main' : 'action.hover',
                size: { xs: 'small', sm: 'medium' }
              }}
            >
              {isMuted ? <MicOff /> : <Mic />}
            </IconButton>
            <IconButton 
              onClick={toggleVideo} 
              sx={{ 
                bgcolor: isVideoOff ? 'error.main' : 'action.hover',
                size: { xs: 'small', sm: 'medium' }
              }}
            >
              {isVideoOff ? <VideocamOff /> : <Videocam />}
            </IconButton>
            <IconButton 
              onClick={endCall} 
              sx={{ 
                bgcolor: 'error.main',
                size: { xs: 'small', sm: 'medium' }
              }}
            >
              <CallEnd />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}